import { Alert, AlertDescription, AlertTitle, Button, Icon, Spinner } from "@qw/ui";
import { getQuoteFollowUpWakeUpTitle, type QuoteFollowUpWakeUpTiming } from "@/utils/quote-follow-up-wake-up";

export interface QuoteFollowUpBannerWakeUp extends QuoteFollowUpWakeUpTiming {
  id: string;
  agentEmail: string;
  agentPhone?: string | null;
  sourceReason?: string;
}

export interface QuoteFollowUpBannerProps {
  dueWakeUp?: QuoteFollowUpBannerWakeUp | null;
  followUpLoading?: boolean;
  markFollowedUpLoading?: boolean;
  onFollowUp: () => void;
  onMarkFollowedUp: () => void;
  showActions?: boolean;
}

export const QuoteFollowUpBanner = ({
  dueWakeUp,
  followUpLoading,
  markFollowedUpLoading,
  onFollowUp,
  onMarkFollowedUp,
  showActions = true,
}: QuoteFollowUpBannerProps) => {
  if (!dueWakeUp || (dueWakeUp.sourceReason && dueWakeUp.sourceReason !== "QuoteFollowUp")) {
    return null;
  }

  const title = getQuoteFollowUpWakeUpTitle(dueWakeUp);

  return (
    <div className="p-3 pb-0">
      <Alert variant="primary" icon="notifications_active" className="mb-4 [&>div:last-child]:min-w-0">
        <div className="flex min-w-0 flex-col gap-4 @4xl/conversation:flex-row @4xl/conversation:items-start @4xl/conversation:justify-between">
          <div className="min-w-0 space-y-2">
            <AlertTitle className="break-words">{title}</AlertTitle>
            <AlertDescription>
              <dl className="grid gap-1 text-sm @2xl/conversation:grid-cols-[auto_minmax(0,1fr)] @2xl/conversation:gap-x-2">
                <dt className="whitespace-nowrap text-primary/75">Agent email</dt>
                <dd className="min-w-0 break-all font-medium">{dueWakeUp.agentEmail}</dd>
                {dueWakeUp.agentPhone && (
                  <>
                    <dt className="whitespace-nowrap text-primary/75">Agent phone</dt>
                    <dd className="min-w-0 break-words font-medium">{dueWakeUp.agentPhone}</dd>
                  </>
                )}
              </dl>
            </AlertDescription>
          </div>
          {showActions && (
            <div className="flex min-w-0 flex-col gap-2 @2xl/conversation:flex-row @4xl/conversation:flex-none @4xl/conversation:justify-end">
              <Button
                type="button"
                size="sm"
                theme="primary"
                onClick={onFollowUp}
                disabled={followUpLoading}
                className="w-full @2xl/conversation:w-auto"
              >
                {followUpLoading ? <Spinner /> : <Icon icon="reply" />}
                Follow up
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                theme="primary"
                onClick={onMarkFollowedUp}
                disabled={markFollowedUpLoading}
                className="w-full @2xl/conversation:w-auto"
              >
                {markFollowedUpLoading ? <Spinner /> : <Icon icon="check" />}
                Mark followed up
              </Button>
            </div>
          )}
        </div>
      </Alert>
    </div>
  );
};
